import ReactDOM from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { generatePath } from 'react-router'

import { useClickOutside } from '~/hooks/useClickOutside'
import { routes } from '~/queries/routes'
import { CategoriesIcon, CloseIcon } from '~/components/icons'
import { Button } from '~/components/general/Button'

import { fadeInFadeOutTw } from '~/styles/mixins'
import { Link } from '@remix-run/react'
import classNames from 'classnames'
import { TypeBlogCategorySkeleton } from '~/types/contentful'
import { Entry } from 'contentful'

export type MobileCategoriesProps = {
  categories: (Entry<TypeBlogCategorySkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined)[]
}

export const MobileCategories = ({ categories }: MobileCategoriesProps) => {
  const [isFirstMount, setFirstMount] = useState(false)
  useEffect(() => setFirstMount(true), [])
  const ref = useRef<HTMLDivElement>(null)
  const [isOpened, setIsOpened] = useState(false)
  const toggle = () => setIsOpened((isOpened) => !isOpened)
  const close = () => setIsOpened(false)
  useClickOutside(ref, close)

  return (
    <div className="inline-block relative text-neutral-100" ref={ref}>
      <Button
        iconLeft={<CategoriesIcon />}
        text={'All Categories'}
        onClick={toggle}
        className={'w-fit'}
      />

      <LaptopCategoriesList isOpened={isOpened} categories={categories} close={close} />

      {isFirstMount &&
        typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <PhoneCategoriesList isOpened={isOpened} categories={categories} close={close} />,
          window.document.body,
        )}
    </div>
  )
}

type CategoriesListProps = MobileCategoriesProps & {
  isOpened: boolean
  close: () => void
}

const LaptopCategoriesList = ({ isOpened, categories, close }: CategoriesListProps) => (
  <div
    className={`shadow-light_high max-md:hidden top-[calc(100%_+_6px)] w-[257px] absolute left-0 text-neutral-100 overflow-hidden z-1 rounded-3xl ${fadeInFadeOutTw(
      isOpened,
    )}`}
  >
    <ul className="p-0 m-0 list-none bg-white max-md:px-5 max-md:pb-8 max-md:rounded-[20px_20px_0_0]">
      <li className="max-md:[&:not(:last-child)]:border-neutral-15 max-md:[&:not(:last-child)]:border-b">
        <Link
          className="text-s flex items-center py-4 mx-6 bg-white max-md:px-0 lg:[&.active]:bg-neutral-10 lg:[&.active]:pointer-events-none"
          style={{ transition: 'background-color 0.35s ease' }}
          to={routes.careerAdvice}
          onClick={close}
        >
          All Posts
        </Link>
      </li>
      {categories.map((category) => (
        <li
          className="max-md:[&:not(:last-child)]:border-neutral-15 max-md:[&:not(:last-child)]:border-b"
          key={category?.fields.slug}
        >
          <Link
            className="text-s flex items-center py-4 mx-6 bg-white max-md:px-0 lg:[&.active]:bg-neutral-10 lg:[&.active]:pointer-events-none"
            style={{ transition: 'background-color 0.35s ease' }}
            onClick={close}
            to={generatePath(routes.resourcesCategory, {
              category: category?.fields.slug,
            })}
          >
            {category?.fields.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
)

const PhoneCategoriesList = ({ isOpened, categories, close }: CategoriesListProps) => (
  <div
    className={classNames(
      'shadow-light_high hidden fixed top-0 left-0 right-0 bottom-0 w-auto text-neutral-100 bg-neutral-100 bg-opacity-90 z-999 overflow-auto pt-15 flex-col max-md:flex',
      fadeInFadeOutTw(isOpened),
    )}
  >
    <div className="flex flex-grow flex-col justify-end">
      <Button iconRight={<CloseIcon />} color={'transparent'} onClick={close} className={'w-fit'} />
      <ul className="p-0 m-0 list-none bg-white max-md:px-5 max-md:pb-8 max-md:rounded-[20px_20px_0_0]">
        <li className="max-md:[&:not(:last-child)]:border-neutral-15 max-md:[&:not(:last-child)]:border-b">
          <Link
            className="text-s flex items-center py-4 mx-6 bg-white max-md:px-0 lg:[&.active]:bg-neutral-10 lg:[&.active]:pointer-events-none"
            style={{ transition: 'background-color 0.35s ease' }}
            to={routes.careerAdvice}
            onClick={close}
          >
            All Posts
          </Link>
        </li>
        {categories.map((category) => (
          <li
            className="max-md:[&:not(:last-child)]:border-neutral-15 max-md:[&:not(:last-child)]:border-b"
            key={category?.fields.slug}
          >
            <Link
              className="text-s flex items-center py-4 mx-6 bg-white max-md:px-0 lg:[&.active]:bg-neutral-10 lg:[&.active]:pointer-events-none"
              style={{ transition: 'background-color 0.35s ease' }}
              onClick={close}
              to={generatePath(routes.resourcesCategory, {
                category: category?.fields.slug,
              })}
            >
              {category?.fields.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
)
